import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Loader2, ShieldCheck, ChevronRight } from 'lucide-react'
import clsx from 'clsx'
import { securityApi, SEVERITIES, severityMeta } from '../../services/securityApi'
import { formatDateTime } from '../../utils/format'

/**
 * Lista de achados de segurança. Use `deviceId` (aba do dispositivo) OU `tenantId` (página geral).
 * Somente leitura: a correção é sempre decisão do operador.
 */
export default function SecurityFindingsList({ deviceId = null, tenantId = null, showDevice = false }) {
  const [severity, setSeverity] = useState('')
  const [includeResolved, setIncludeResolved] = useState(false)
  const [expanded, setExpanded] = useState(null)

  const query = useQuery({
    queryKey: ['securityFindings', deviceId, tenantId, includeResolved],
    enabled: !!(deviceId || tenantId),
    queryFn: () => (deviceId
      ? securityApi.listByDevice(deviceId, { includeResolved })
      : securityApi.listByTenant(tenantId, { includeResolved })),
  })

  const items = useMemo(() => {
    const all = query.data || []
    return severity ? all.filter((f) => f.severity === severity) : all
  }, [query.data, severity])

  const counts = useMemo(() => {
    const open = (query.data || []).filter((f) => !f.resolvedAt)
    return SEVERITIES.reduce((acc, s) => ({ ...acc, [s]: open.filter((f) => f.severity === s).length }), {})
  }, [query.data])

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setSeverity('')}
          className={clsx('px-3 py-1 rounded-full border text-xs',
            severity === '' ? 'border-theme-primary text-theme-primary bg-theme-primary/10' : 'border-gray-200 text-gray-600 bg-white')}
        >
          Todos
        </button>
        {SEVERITIES.map((s) => (
          <button
            key={s}
            onClick={() => setSeverity(severity === s ? '' : s)}
            className={clsx('px-3 py-1 rounded-full border text-xs inline-flex items-center gap-1.5',
              severity === s ? 'border-theme-primary text-theme-primary bg-theme-primary/10' : 'border-gray-200 text-gray-600 bg-white')}
          >
            <span className={clsx('w-2 h-2 rounded-full', severityMeta(s).dot)} />
            {severityMeta(s).label} ({counts[s] || 0})
          </button>
        ))}
        <label className="ml-auto flex items-center gap-1.5 text-xs text-gray-600">
          <input type="checkbox" checked={includeResolved} onChange={(e) => setIncludeResolved(e.target.checked)} />
          Mostrar resolvidos
        </label>
      </div>

      {query.isLoading ? (
        <div className="flex items-center justify-center py-10 text-gray-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Carregando achados…
        </div>
      ) : query.isError ? (
        <div className="p-4 text-sm text-red-700 bg-white border border-gray-200 rounded-lg">
          Não foi possível carregar os achados.
        </div>
      ) : items.length === 0 ? (
        <div className="py-10 text-center text-sm text-gray-500 bg-white border border-gray-200 rounded-lg">
          <ShieldCheck className="w-8 h-8 mx-auto mb-2 text-green-500" />
          Nenhum achado {severity ? `de severidade ${severityMeta(severity).label.toLowerCase()}` : 'em aberto'}.
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
          {items.map((f) => {
            const meta = severityMeta(f.severity)
            const open = expanded === f.id
            return (
              <div key={f.id} className={clsx('p-3', f.resolvedAt && 'opacity-60')}>
                <button className="w-full flex items-start gap-3 text-left" onClick={() => setExpanded(open ? null : f.id)}>
                  <span className={clsx('mt-0.5 px-1.5 py-0.5 rounded text-[11px] font-medium shrink-0', meta.cls)}>
                    {meta.label}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm text-gray-900">
                      {f.title}
                      {f.resolvedAt && <span className="ml-2 text-xs text-green-700">resolvido</span>}
                    </span>
                    <span className="block text-xs text-gray-500">
                      <span className="font-mono">{f.code}</span>
                      {showDevice && (
                        <>
                          {' · '}
                          <Link to={`/managed-devices/${f.managedDeviceId}`} className="hover:text-theme-primary" onClick={(e) => e.stopPropagation()}>
                            {f.deviceName}
                          </Link>
                        </>
                      )}
                      {' · visto em '}{formatDateTime(f.lastSeenAt)}
                    </span>
                  </span>
                  <ChevronRight className={clsx('w-4 h-4 text-gray-400 shrink-0 transition-transform', open && 'rotate-90')} />
                </button>
                {open && (
                  <div className="mt-2 ml-[4.5rem] space-y-1 text-xs text-gray-600">
                    <p>{f.detail}</p>
                    {f.evidence && (
                      <pre className="p-2 bg-gray-50 border border-gray-200 rounded font-mono whitespace-pre-wrap break-all text-[11px]">
                        {f.evidence}
                      </pre>
                    )}
                    <p className="text-gray-400">
                      Detectado em {formatDateTime(f.firstSeenAt)}
                      {f.resolvedAt && ` · resolvido em ${formatDateTime(f.resolvedAt)}`}
                    </p>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
